import React from 'react';
import { X, UserPlus, Settings } from 'lucide-react';

const MAX_SIGNUP_INVITES = 2;

const field =
  'w-full border border-[#d1d1d6] rounded-xl px-3.5 py-2.5 text-[14px] text-[#1d1d1f] ' +
  'placeholder-[#aeaeb2] focus:outline-none focus:border-[#0071e3] focus:ring-2 ' +
  'focus:ring-[#0071e3]/20 transition-all bg-white';

const errField = field + ' border-red-400 focus:border-red-500 focus:ring-red-200';

const label = 'block text-[13px] font-medium text-[#3a3a3c] mb-1';

const TeamSetupStep = ({ formData, onInviteChange, onAddInvite, onRemoveInvite, onSkip, errors }) => {
  const invites = formData.teamInvites || [];
  const atLimit = invites.length >= MAX_SIGNUP_INVITES;

  return (
    <div>
      <p className="text-[13px] text-[#6e6e73] mb-5">
        Add up to {MAX_SIGNUP_INVITES} co-owners to your organization. You can invite the rest of your team with specific roles from{' '}
        <span className="font-medium text-[#1d1d1f]">Settings → Users</span>.
      </p>

      <div className="space-y-4">
        {invites.map((row, idx) => {
          const rowErrors = errors[`teamInvites[${idx}]`] || {};
          return (
            <div
              key={idx}
              className="relative border border-[#e5e5ea] rounded-2xl p-4 bg-[#fafafa]"
            >
              {invites.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemoveInvite(idx)}
                  className="absolute top-3 right-3 text-[#aeaeb2] hover:text-[#1d1d1f] transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#e8e8ed]"
                  aria-label="Remove"
                >
                  <X size={14} />
                </button>
              )}

              <p className="text-[12px] font-semibold text-[#6e6e73] uppercase tracking-wide mb-3">
                Person {idx + 1}
              </p>

              {/* Row 1: First + Last name */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className={label}>First Name</label>
                  <input
                    className={rowErrors.firstName ? errField : field}
                    placeholder="First name"
                    value={row.firstName || ''}
                    onChange={e => onInviteChange(idx, 'firstName', e.target.value)}
                  />
                  {rowErrors.firstName && (
                    <p className="text-[12px] text-red-500 mt-1">{rowErrors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className={label}>Last Name</label>
                  <input
                    className={rowErrors.lastName ? errField : field}
                    placeholder="Last name"
                    value={row.lastName || ''}
                    onChange={e => onInviteChange(idx, 'lastName', e.target.value)}
                  />
                  {rowErrors.lastName && (
                    <p className="text-[12px] text-red-500 mt-1">{rowErrors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Row 2: Domain + Email */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className={label}>Domain</label>
                  <input
                    className={field}
                    placeholder="company.com"
                    value={row.domain || ''}
                    onChange={e => onInviteChange(idx, 'domain', e.target.value)}
                  />
                </div>
                <div>
                  <label className={label}>
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={rowErrors.email ? errField : field}
                    type="email"
                    placeholder={row.domain ? `name@${row.domain}` : 'name@company.com'}
                    value={row.email || ''}
                    onChange={e => onInviteChange(idx, 'email', e.target.value)}
                  />
                  {rowErrors.email && (
                    <p className="text-[12px] text-red-500 mt-1">{rowErrors.email}</p>
                  )}
                </div>
              </div>

              {/* Row 3: Contact number + Role */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Phone Number</label>
                  <input
                    className={field}
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={row.contactNumber || ''}
                    onChange={e => onInviteChange(idx, 'contactNumber', e.target.value)}
                  />
                </div>
                <div>
                  <label className={label}>Role</label>
                  <div
                    className={
                      'w-full border border-[#d1d1d6] rounded-xl px-3.5 py-2.5 text-[14px] ' +
                      'text-[#1d1d1f] bg-[#f5f5f7] flex items-center justify-between select-none'
                    }
                  >
                    <span>Owner</span>
                    <span className="text-[11px] text-[#aeaeb2] font-medium">Fixed</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Skip + Add another */}
      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={onSkip}
          className="text-[13px] text-[#6e6e73] hover:text-[#1d1d1f] transition-colors underline underline-offset-2"
        >
          Skip this step
        </button>

        {atLimit ? (
          <div className="flex items-center gap-2 text-[13px] text-[#6e6e73] bg-[#f5f5f7] border border-[#e5e5ea] rounded-xl px-3.5 py-2">
            <Settings size={14} className="shrink-0 text-[#0071e3]" />
            <span>
              Invite more team members with other roles from{' '}
              <span className="font-medium text-[#1d1d1f]">Settings → Users</span>
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={onAddInvite}
            className="flex items-center gap-1.5 text-[13px] font-medium text-[#0071e3] hover:text-[#0077ed] transition-colors"
          >
            <UserPlus size={15} />
            Add another person
          </button>
        )}
      </div>
    </div>
  );
};

export default TeamSetupStep;
